import type { DecisionState, Status } from "./api";

/**
 * The status language, in one place, because it is the same language in three
 * places: a chip, a map polygon, and a legend.
 *
 * ADR 0001. Hue is decoration. Every state carries a *shape* and a *word*:
 * permitted and in-progress are only 1.10:1 apart in greyscale, and a
 * blocked fill drops under the usable contrast floor below 60% opacity. So the
 * three states read as solid / dashed / heavy, with an icon and a label, and the
 * colour is the thing that makes them faster to read rather than the thing that
 * makes them readable at all.
 */

export type StatusVisual = {
  /** The canonical interface label. Fixed in CONTEXT.md, part of the definition. */
  label: string;
  /** Compact form for chips and filters, where the long label crowds the row. */
  short: string;
  icon: string;
  /** MapLibre line-dasharray. Empty = solid. */
  dash: number[];
  /** Heavier stroke on the blocked state, so it is distinct with no colour at all. */
  width: number;
  color: string;
  fill: string;
  /** What the pattern means, for the legend. */
  patternWord: string;
};

/**
 * `blocked` reads **Tertolak**. It once read Tertahan: an area is blocked when
 * a required approval is refused *or* when it is stuck, and "Tertolak" asserts
 * a refusal that may never have happened. The team chose the refusal word
 * anyway (Oct 2026) -- it is their word for a blocked area -- so the
 * distinction now lives only in `refused` decisions ("Ditolak"), never in
 * area status. History kept so nobody "fixes" this back.
 */
export const STATUS: Record<Status, StatusVisual> = {
  permitted: {
    label: "Izin Lokasi Terbit",
    short: "Diizinkan",
    icon: "✓",
    dash: [],
    width: 2,
    color: "#4ADE80",
    fill: "rgba(74, 222, 128, 0.6)",
    patternWord: "garis penuh",
  },
  in_progress: {
    label: "In Progress",
    short: "In Progress",
    icon: "◔",
    dash: [2, 2],
    width: 2,
    color: "#FBBF24",
    fill: "rgba(251, 191, 36, 0.6)",
    patternWord: "garis putus",
  },
  blocked: {
    label: "Tertolak",
    short: "Tertolak",
    icon: "✕",
    dash: [],
    width: 3,
    color: "#F87171",
    fill: "rgba(248, 113, 113, 0.6)",
    patternWord: "garis tebal",
  },
};

export const STATUS_ORDER: Status[] = ["blocked", "in_progress", "permitted"];

export const DECISION: Record<
  DecisionState,
  { label: string; icon: string; color: string; verb: string }
> = {
  granted: { label: "Disetujui", icon: "✓", color: "#4ADE80", verb: "disetujui" },
  refused: { label: "Ditolak", icon: "✕", color: "#F87171", verb: "ditolak" },
};

export const SLOT_KIND_LABEL: Record<string, string> = {
  consent: "Persetujuan pemilik",
  attestation: "Pernyataan / surat",
  internal: "Internal",
};

/** "RT 03 / RW 07" style, the way an Indonesian address is actually read. */
export function placeLine(a: {
  rt?: string | null;
  rw?: string | null;
  kelurahan: string;
  kecamatan: string;
}): string {
  const parts: string[] = [];
  if (a.rw || a.rt) parts.push([a.rt, a.rw].filter(Boolean).join(" / "));
  parts.push(`Kel. ${a.kelurahan}`, `Kec. ${a.kecamatan}`);
  return parts.join(" · ");
}

export function addressTitle(a: { address_detail?: string | null; kelurahan: string }): string {
  return a.address_detail?.trim() || `Kel. ${a.kelurahan}`;
}

export function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Square metres, whole, Indonesian thousand grouping, no decimals.
 *
 * The certificate governs (`luas 500 m2`), so both surfaces print plain m2,
 * e.g. `15.900 m2`.
 */
export function fmtAreaM2(m2: number): string {
  if (!Number.isFinite(m2)) return "—";
  const whole = Math.round(m2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${whole} m2`;
}

export function fmtBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function relTime(iso: string): string {
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 1) return "baru saja";
  if (mins < 60) return `${mins} menit lalu`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} jam lalu`;
  return `${Math.round(hrs / 24)} hari lalu`;
}
